import { supabase } from "~/integrations/supabase/client";
import type {
  MaquilaArea,
  MaquilaClientOption,
  MaquilaConsumption,
  MaquilaConsumptionFilters,
  MaquilaConsumptionInput,
  MaquilaItem,
  MaquilaItemFormData,
  MaquilaInventory,
  MaquilaInventoryFilters,
  MaquilaMovement,
  MaquilaMovementFilters,
  MaquilaMovementFormData,
  MaquilaProductRecipe,
  MaquilaRecipeFormData,
} from "~/types/maquila";

// maquila_product_recipes / maquila_recipe_ingredients / maquila_consumptions /
// maquila_consumption_items are not in the generated Supabase types yet, so the
// table sources and the RPC are cast with `as never` (same pattern as
// productTransferService's fg_plant_transfers).
const RECIPE_SELECT = `
  *,
  client:client_id(id, name),
  ingredients:maquila_recipe_ingredients(
    *,
    oil_type:oil_types(id, code, name, density_kg_per_liter, average_cost_clp),
    raw_material:raw_materials(id, code, name, unit, average_cost_clp)
  )
`;

const CONSUMPTION_SELECT = `
  *,
  client:client_id(id, name),
  performed_by_profile:performed_by(id, name),
  items:maquila_consumption_items(
    *,
    oil_type:oil_types(id, code, name),
    raw_material:raw_materials(id, code, name)
  )
`;

export const maquilaService = {
  // ============================================
  // AREAS
  // ============================================

  async getAreas(): Promise<MaquilaArea[]> {
    const { data, error } = await supabase
      .from("maquila_areas")
      .select("*")
      .eq("is_active", true)
      .order("display_order");

    if (error) throw error;
    return data as MaquilaArea[];
  },

  // ============================================
  // CLIENTS
  // ============================================

  async getMaquilaClients(): Promise<MaquilaClientOption[]> {
    const { data, error } = await supabase
      .from("clients")
      .select("id, name, tax_id")
      .eq("is_maquila_client", true)
      .order("name");

    if (error) throw error;
    return (data ?? []).map((c) => ({
      id: c.id,
      name: c.name ?? "",
      tax_id: c.tax_id ?? null,
    }));
  },

  // ============================================
  // ITEMS
  // ============================================

  async getItems(clientId?: string): Promise<MaquilaItem[]> {
    let query = supabase
      .from("maquila_items")
      .select(
        `
        *,
        client:client_id(id, name)
      `
      )
      .eq("is_active", true)
      .order("name");

    if (clientId) {
      query = query.eq("client_id", clientId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as MaquilaItem[];
  },

  async getItemById(id: string): Promise<MaquilaItem> {
    const { data, error } = await supabase
      .from("maquila_items")
      .select(
        `
        *,
        client:client_id(id, name)
      `
      )
      .eq("id", id)
      .single();

    if (error) throw error;
    return data as MaquilaItem;
  },

  async createItem(formData: MaquilaItemFormData): Promise<MaquilaItem> {
    const { data, error } = await supabase
      .from("maquila_items")
      .insert(formData)
      .select(
        `
        *,
        client:client_id(id, name)
      `
      )
      .single();

    if (error) throw error;
    return data as MaquilaItem;
  },

  async updateItem(
    id: string,
    formData: Partial<MaquilaItemFormData>
  ): Promise<MaquilaItem> {
    const { data, error } = await supabase
      .from("maquila_items")
      .update({ ...formData, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select(
        `
        *,
        client:client_id(id, name)
      `
      )
      .single();

    if (error) throw error;
    return data as MaquilaItem;
  },

  async deleteItem(id: string): Promise<void> {
    const { error } = await supabase
      .from("maquila_items")
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq("id", id);

    if (error) throw error;
  },

  // ============================================
  // INVENTORY
  // ============================================

  async getInventory(filters?: MaquilaInventoryFilters): Promise<MaquilaInventory[]> {
    let query = supabase
      .from("maquila_inventory")
      .select(
        `
        *,
        maquila_item:maquila_item_id(*, client:client_id(id, name)),
        maquila_area:maquila_area_id(*)
      `
      )
      .gt("quantity", 0);

    if (filters?.area_id) {
      query = query.eq("maquila_area_id", filters.area_id);
    }

    const { data, error } = await query.order("maquila_area_id");
    if (error) throw error;

    // Filter by client_id if specified (need to do this client-side due to nested filter)
    let result = data as MaquilaInventory[];
    if (filters?.client_id) {
      result = result.filter(
        (inv) => inv.maquila_item?.client_id === filters.client_id
      );
    }

    return result;
  },

  async getInventoryByArea(): Promise<Record<string, MaquilaInventory[]>> {
    const { data, error } = await supabase
      .from("maquila_inventory")
      .select(
        `
        *,
        maquila_item:maquila_item_id(*, client:client_id(id, name)),
        maquila_area:maquila_area_id(*)
      `
      )
      .gt("quantity", 0);

    if (error) throw error;

    // Group by area
    const byArea: Record<string, MaquilaInventory[]> = {};
    for (const inv of data as MaquilaInventory[]) {
      const areaId = inv.maquila_area_id;
      if (!byArea[areaId]) byArea[areaId] = [];
      byArea[areaId].push(inv);
    }
    return byArea;
  },

  async getItemInventory(itemId: string): Promise<MaquilaInventory[]> {
    const { data, error } = await supabase
      .from("maquila_inventory")
      .select(
        `
        *,
        maquila_area:maquila_area_id(*)
      `
      )
      .eq("maquila_item_id", itemId)
      .gt("quantity", 0);

    if (error) throw error;
    return data as MaquilaInventory[];
  },

  // ============================================
  // MOVEMENTS
  // ============================================

  async getMovements(filters?: MaquilaMovementFilters): Promise<MaquilaMovement[]> {
    let query = supabase
      .from("maquila_movements")
      .select(
        `
        *,
        maquila_item:maquila_item_id(*),
        client:client_id(id, name),
        from_area:from_area_id(*),
        to_area:to_area_id(*),
        performed_by_profile:performed_by(id, name)
      `
      )
      .order("created_at", { ascending: false });

    if (filters?.client_id) {
      query = query.eq("client_id", filters.client_id);
    }
    if (filters?.maquila_item_id) {
      query = query.eq("maquila_item_id", filters.maquila_item_id);
    }
    if (filters?.movement_type) {
      query = query.eq("movement_type", filters.movement_type);
    }
    if (filters?.date_from) {
      query = query.gte("movement_date", filters.date_from);
    }
    if (filters?.date_to) {
      query = query.lte("movement_date", filters.date_to);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as MaquilaMovement[];
  },

  async createMovement(formData: MaquilaMovementFormData): Promise<string> {
    const { data, error } = await supabase.rpc("register_maquila_movement", {
      p_movement_type: formData.movement_type,
      p_maquila_item_id: formData.maquila_item_id,
      p_from_area_id: formData.from_area_id ?? null,
      p_to_area_id: formData.to_area_id ?? null,
      p_quantity: formData.quantity,
      p_notes: formData.notes ?? null,
      p_reference_document: formData.reference_document ?? null,
    });

    if (error) throw error;
    return data as string;
  },

  // ============================================
  // PRODUCT RECIPES (BOM of Aliace insumos per maquila client product)
  // ============================================

  async getRecipes(clientId?: string): Promise<MaquilaProductRecipe[]> {
    let query = supabase
      .from("maquila_product_recipes" as never)
      .select(RECIPE_SELECT)
      .eq("is_active", true)
      .order("product_name");

    if (clientId) {
      query = query.eq("client_id", clientId) as typeof query;
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as unknown as MaquilaProductRecipe[];
  },

  async getRecipeById(id: string): Promise<MaquilaProductRecipe> {
    const { data, error } = await supabase
      .from("maquila_product_recipes" as never)
      .select(RECIPE_SELECT)
      .eq("id", id)
      .single();

    if (error) throw error;
    return data as unknown as MaquilaProductRecipe;
  },

  async createRecipe(
    formData: MaquilaRecipeFormData
  ): Promise<MaquilaProductRecipe> {
    const { data: recipe, error } = await supabase
      .from("maquila_product_recipes" as never)
      .insert({
        client_id: formData.client_id,
        product_name: formData.product_name,
        product_code: formData.product_code || null,
        output_unit: formData.output_unit,
        notes: formData.notes || null,
      } as never)
      .select("id")
      .single();

    if (error) throw error;
    const recipeId = (recipe as { id: string }).id;

    await this.replaceRecipeIngredients(recipeId, formData);
    return this.getRecipeById(recipeId);
  },

  async updateRecipe(
    id: string,
    formData: MaquilaRecipeFormData
  ): Promise<MaquilaProductRecipe> {
    const { error } = await supabase
      .from("maquila_product_recipes" as never)
      .update({
        client_id: formData.client_id,
        product_name: formData.product_name,
        product_code: formData.product_code || null,
        output_unit: formData.output_unit,
        notes: formData.notes || null,
        updated_at: new Date().toISOString(),
      } as never)
      .eq("id", id);

    if (error) throw error;

    await this.replaceRecipeIngredients(id, formData);
    return this.getRecipeById(id);
  },

  // maquila_recipe_ingredients aren't referenced by any other table, so a full
  // delete + reinsert is the simplest safe way to persist an edited BOM.
  async replaceRecipeIngredients(
    recipeId: string,
    formData: MaquilaRecipeFormData
  ): Promise<void> {
    const { error: delError } = await supabase
      .from("maquila_recipe_ingredients" as never)
      .delete()
      .eq("recipe_id", recipeId);
    if (delError) throw delError;

    const rows = (formData.ingredients ?? [])
      .filter((i) => i.oil_type_id || i.raw_material_id)
      .map((i) => ({
        recipe_id: recipeId,
        ingredient_type: i.ingredient_type,
        oil_type_id: i.ingredient_type === "oil" ? i.oil_type_id || null : null,
        raw_material_id:
          i.ingredient_type === "raw_material"
            ? i.raw_material_id || null
            : null,
        quantity_per_unit: i.quantity_per_unit,
        unit: i.unit,
        waste_factor: i.waste_factor || 1,
        notes: i.notes || null,
      }));

    if (rows.length > 0) {
      const { error: insError } = await supabase
        .from("maquila_recipe_ingredients" as never)
        .insert(rows as never);
      if (insError) throw insError;
    }
  },

  async deleteRecipe(id: string): Promise<void> {
    const { error } = await supabase
      .from("maquila_product_recipes" as never)
      .update({ is_active: false, updated_at: new Date().toISOString() } as never)
      .eq("id", id);

    if (error) throw error;
  },

  // ============================================
  // CONSUMPTION RUNS (deduct Aliace insumos)
  // ============================================

  async createConsumption(input: MaquilaConsumptionInput): Promise<string> {
    const { data, error } = await supabase.rpc(
      "register_maquila_consumption" as never,
      {
        p_client_id: input.client_id,
        p_recipe_id: input.recipe_id ?? null,
        p_product_name: input.product_name,
        p_quantity_produced: input.quantity_produced,
        p_plant: input.plant,
        p_lines: input.lines,
        p_notes: input.notes ?? null,
        p_reference_document: input.reference_document ?? null,
        p_consumption_date: input.consumption_date ?? null,
      } as never
    );

    if (error) throw error;
    return data as unknown as string;
  },

  async getConsumptions(
    filters?: MaquilaConsumptionFilters
  ): Promise<MaquilaConsumption[]> {
    let query = supabase
      .from("maquila_consumptions" as never)
      .select(CONSUMPTION_SELECT)
      .order("created_at", { ascending: false });

    if (filters?.client_id) {
      query = query.eq("client_id", filters.client_id) as typeof query;
    }
    if (filters?.date_from) {
      query = query.gte("consumption_date", filters.date_from) as typeof query;
    }
    if (filters?.date_to) {
      query = query.lte("consumption_date", filters.date_to) as typeof query;
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as unknown as MaquilaConsumption[];
  },

  async getConsumptionById(id: string): Promise<MaquilaConsumption> {
    const { data, error } = await supabase
      .from("maquila_consumptions" as never)
      .select(CONSUMPTION_SELECT)
      .eq("id", id)
      .single();

    if (error) throw error;
    return data as unknown as MaquilaConsumption;
  },
};
